import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TaskListItem } from '@/components/TaskListItem';
import { useFilteredTasks, useSortedTasks } from '@/hooks/useFilteredTasks';
import { Button } from "@/components/ui/button";
import { ChevronUp, ChevronRight, ChevronDown } from "lucide-react";
import { useState, useEffect, useMemo } from 'react';
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/components/ui/use-toast"
import { QuickAddTask } from '@/components/QuickAddTask';
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea, ScrollBar } from './ui/scroll-area';

function getWeekNumber(d) {
    // Copy the date to avoid modifying the original
    const date = new Date(d);
    date.setHours(0, 0, 0, 0);
    // Thursday in current week decides the year
    date.setDate(date.getDate() + 3 - (date.getDay() + 6) % 7);
    // January 4 is always in week 1
    const week1 = new Date(date.getFullYear(), 0, 4);
    // Adjust to Thursday in week 1 and count number of weeks from date to week1
    return 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
}

export function ProjectList({ isLoading, project, filterByAssignee = new Set(), filterBy, user, setProject, sortBy, groupByAssignee, showBacklogColumn }) {
    if (isLoading || !project) {
        return (
            <div className="space-y-6 pb-6">
                {/* Skeleton for the "Load Previous Week" button */}
                <div className="flex justify-center">
                    <Skeleton className="h-8 w-40 rounded" />
                </div>
                {/* Skeleton for the Active Week Section */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center justify-between">
                            {/* Left side week title skeleton */}
                            <Skeleton className="h-6 w-32" />
                            {/* Right side week range skeleton */}
                            <Skeleton className="h-4 w-40" />
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            {[...Array(3)].map((_, index) => (
                                // Reduced height for list items from 80px to 60px
                                <Skeleton key={index} className="h-[60px] w-full" />
                            ))}
                        </div>
                    </CardContent>
                </Card>
                {/* Skeleton for the Backlog Section */}
                <Card>
                    <CardHeader>
                        <CardTitle>
                            <Skeleton className="h-6 w-32" />
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            {[...Array(2)].map((_, index) => (
                                // Reduced height for list items from 80px to 60px
                                <Skeleton key={index} className="h-[60px] w-full" />
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const { axiosInstance } = useAuth();
    const { toast } = useToast()
    const [loadingPrevious, setLoadingPrevious] = useState(false);
    const [loadingNext, setLoadingNext] = useState(false);
    const [previousWeeks, setPreviousWeeks] = useState([]);
    const [selectedTasks, setSelectedTasks] = useState(new Set());
    const [isDraggingAny, setIsDraggingAny] = useState(false);

    let filteredTasks = useFilteredTasks(project, {
        filterByAssignee,
        filterBy,
        user
    });
    filteredTasks = useSortedTasks(filteredTasks, sortBy);

    useEffect(() => {
        setActiveWeekTasks(
            sortTasksByStatusAndPosition(
                filteredTasks.filter(task => task.status?.id !== 'backlog')
            )
        );
        setBacklogTasks(
            sortTasksByStatusAndPosition(
                filteredTasks.filter(task => task.status?.id === 'backlog')
            )
        );
    }, [filteredTasks]);

    // Helper function to sort tasks by status and position
    const sortTasksByStatusAndPosition = (tasks) => {
        // Get status order from the project
        const statusOrder = project?.statuses?.map(status => status.id);
        
        let sortedTasks = [...tasks];
        if(sortBy === 'manual') {
            sortedTasks.sort((a, b) => {
                const statusA = statusOrder.indexOf(a.status.id);
                const statusB = statusOrder.indexOf(b.status.id);
                if (statusA !== statusB) return statusA - statusB;
                return 0; // Add return for position comparison if needed
            });
        }
        if(groupByAssignee) {
            sortedTasks.sort((a, b) => {
                const assigneeA = a.assignee?.name;
                const assigneeB = b.assignee?.name;
                if(!assigneeA) return 1;
                if(!assigneeB) return -1;
                if (assigneeA !== assigneeB) return assigneeA.localeCompare(assigneeB);
                return 0;
            });
        }
        return sortedTasks;
    };

    // Separate tasks into active week and backlog
    const [activeWeekTasks, setActiveWeekTasks] = useState(
        sortTasksByStatusAndPosition(
            filteredTasks.filter(task => task.status?.id !== 'backlog')
        )
    );
    const [backlogTasks, setBacklogTasks] = useState(
        sortTasksByStatusAndPosition(
            filteredTasks.filter(task => task.status?.id === 'backlog')
        )
    );

    const loadPreviousWeek = async () => {
        setLoadingPrevious(true);
        try {
            // Get the earliest loaded week's start date
            const firstWeek = previousWeeks.length > 0
                ? previousWeeks[0]
                : project.week;

            const startDate = new Date(firstWeek.startDate);

            // Create a new date for the previous week by subtracting 7 days from the start date
            const prevWeekDate = new Date(startDate);
            prevWeekDate.setDate(startDate.getDate() - 7);

            const prevWeek = getWeekNumber(prevWeekDate);
            const prevYear = prevWeekDate.getFullYear();

            const response = await axiosInstance.get('/tasks/query', {
                params: {
                    org: project.organizationSlug,
                    project: project.key,
                    week: prevWeek,
                    year: prevYear
                }
            });

            // Store the actual startDate in the week data
            setPreviousWeeks(prev => [{
                ...response.data.week,
                tasks: response.data.tasks,
                startDate: prevWeekDate.toISOString() // Ensure we store the actual date we calculated
            }, ...prev]);

            toast({
                description: "Previous week loaded successfully",
            });
        } catch (error) {
            console.error('Error loading previous week:', error);
            toast({
                variant: "destructive",
                description: "Failed to load previous week",
            });
        } finally {
            setLoadingPrevious(false);
        }
    };

    const loadNextWeek = async () => {
        setLoadingNext(true);
        try {
            // TODO: Implement loading next week
            await new Promise(resolve => setTimeout(resolve, 1000)); // Temporary simulation
        } catch (error) {
            console.error('Error loading next week:', error);
            toast({
                variant: "destructive",
                description: "Failed to load next week",
            });
        } finally {
            setLoadingNext(false);
        }
    };

    const handleTaskUpdate = (updatedTask) => {
        setProject((prevProject) => ({
            ...prevProject,
            tasks: prevProject.tasks.map((task) =>
                task.id === updatedTask.id
                    ? {
                        ...task,
                        ...updatedTask,
                        status: updatedTask.status,
                        assignee: updatedTask.assignee || null,
                        priority: updatedTask.priority,
                    }
                    : task
            ),
        }));
    };

    const onDragStart = () => {
        setIsDraggingAny(true);
    };

    const onDragEnd = async (result) => {
        setIsDraggingAny(false);
        if (!result.destination) return;

        const { source, destination, draggableId } = result;

        if (source.droppableId === destination.droppableId &&
            source.index === destination.index) {
            return;
        }

        // Get the primary task being dragged from the appropriate list instead of filteredTasks
        const sourceList = source.droppableId === 'active' ? activeWeekTasks : backlogTasks;
        const taskToMove = sourceList.find(task => task.id === draggableId);
        if (!taskToMove) return;

        // Get all selected tasks if the dragged task is part of the selection
        const tasksToMove = selectedTasks.has(draggableId)
            ? sourceList.filter(task => selectedTasks.has(task.id))
            : [taskToMove];

        try {
            let newStatus;

            // Determine the new status based on destination
            if (destination.droppableId === 'backlog') {
                newStatus = 'backlog';
            } else if (destination.droppableId === 'active' && source.droppableId === 'backlog') {
                newStatus = 'todo';
            } else {
                newStatus = taskToMove.status.id;
            }

            // Create updated versions of both lists
            let newActiveWeekTasks = [...activeWeekTasks];
            let newBacklogTasks = [...backlogTasks];

            // Remove tasks from source list
            if (source.droppableId === 'active') {
                newActiveWeekTasks = newActiveWeekTasks.filter(task => !tasksToMove.some(t => t.id === task.id));
            } else {
                newBacklogTasks = newBacklogTasks.filter(task => !tasksToMove.some(t => t.id === task.id));
            }

            // Add tasks to destination list
            const updatedTasksToMove = tasksToMove.map(task => ({
                ...task,
                status: { id: newStatus }
            }));

            if (destination.droppableId === 'active') {
                newActiveWeekTasks.splice(destination.index, 0, ...updatedTasksToMove);
            } else {
                newBacklogTasks.splice(destination.index, 0, ...updatedTasksToMove);
            }

            // Update both lists at once
            setActiveWeekTasks(newActiveWeekTasks);
            setBacklogTasks(newBacklogTasks);

            // Update project state
            setProject(prevProject => ({
                ...prevProject,
                tasks: prevProject.tasks.map(task =>
                    tasksToMove.find(t => t.id === task.id)
                        ? {
                            ...task,
                            status: { id: newStatus },
                            position: destination.index + tasksToMove.findIndex(t => t.id === task.id)
                        }
                        : task
                )
            }));

            // Make API calls for all tasks
            await Promise.all(tasksToMove.map((task, index) =>
                axiosInstance.patch(`/tasks/${project.organizationSlug}/${task.key}`, {
                    status: newStatus,
                    position: destination.index + index,
                    sourceColumnId: source.droppableId,
                    destinationColumnId: destination.droppableId
                })
            ));

            toast({
                description: `${tasksToMove.length} task${tasksToMove.length > 1 ? 's' : ''} moved successfully`,
            });
        } catch (error) {
            console.error('Error moving tasks:', error);
            // Revert the UI changes on error
            setActiveWeekTasks(sortTasksByStatusAndPosition(filteredTasks.filter(task => task.status?.id !== 'backlog')));
            setBacklogTasks(sortTasksByStatusAndPosition(filteredTasks.filter(task => task.status?.id === 'backlog')));
            toast({
                variant: "destructive",
                description: "Failed to move tasks",
            });
        }
    };

    const handleTaskSelection = (taskId, isChecked) => {
        setSelectedTasks(prev => {
            const newSelection = new Set(prev);
            if (isChecked) {
                newSelection.add(taskId);
            } else {
                newSelection.delete(taskId);
            }
            return newSelection;
        });
    };

    const handleTaskCreated = (newTask) => {
        setProject(prevProject => ({
            ...prevProject,
            tasks: [...prevProject.tasks, newTask]
        }));

        // Update the appropriate task list
        if (newTask.status.id === 'backlog') {
            setBacklogTasks(prev => sortTasksByStatusAndPosition([...prev, newTask]));
        } else {
            setActiveWeekTasks(prev => sortTasksByStatusAndPosition([...prev, newTask]));
        }
    };

    return (
        <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
            <div className="space-y-6 pb-6">
                {/* Load Previous Week Button */}
                <div className="flex justify-center">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={loadPreviousWeek}
                        disabled={loadingPrevious}
                    >
                        {loadingPrevious ? (
                            "Loading..."
                        ) : (
                            <>
                                <ChevronUp className="w-4 h-4 mr-2" />
                                Browse Previous Week
                            </>
                        )}
                    </Button>
                </div>

                {/* Previous Weeks Sections */}
                {previousWeeks.map((week, index) => (
                    <Card key={`${week.year}-${week.number}`}>
                        <CardHeader>
                            <CardTitle className="flex items-center justify-between">
                                <div className="text-lg">
                                    Week {week.number} {' • '} Previous Week
                                </div>
                                <div className="text-sm text-muted-foreground font-normal">
                                    {week.startDateFormatted} - {week.endDateFormatted}
                                </div>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <ScrollArea className="w-full">
                                <div className="space-y-2">
                                {week.tasks.length === 0 ? (
                                    <p className="text-sm text-muted-foreground text-center py-4">
                                        No completed tasks this week
                                    </p>
                                ) : (
                                    week.tasks.map((task, taskIndex) => (
                                        <TaskListItem
                                            key={task.id}
                                            task={task}
                                            organizationSlug={project.organizationSlug}
                                            onTaskUpdate={handleTaskUpdate}
                                            onSelectionChange={handleTaskSelection}
                                            isSelected={selectedTasks.has(task.id)}
                                            isSelectedButNotDragging={selectedTasks.has(task.id) && isSelected && !isMainDragTarget}
                                        />
                                    ))
                                    )}
                                </div>
                                <ScrollBar orientation="horizontal" />
                            </ScrollArea>
                        </CardContent>
                    </Card>
                ))}

                {/* Current Week Section */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center justify-between">
                            <div className="text-lg">
                                Week {project.week.weekNumber} {' • '} Active
                            </div>
                            <div className="text-sm text-muted-foreground font-normal">
                                {project.week.startDate} - {project.week.endDate}
                            </div>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2">
                            <Droppable droppableId="active">
                                {(provided) => (
                                    <div
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                    className="space-y-2"
                                    >

                                            {activeWeekTasks.map((task, index) => (
                                                <Draggable
                                                    key={task.id}
                                                draggableId={task.id}
                                                index={index}
                                            >
                                                {(provided, snapshot) => (
                                        <ScrollArea className="w-full">
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                    >
                                                        <TaskListItem
                                                            task={task}
                                                            organizationSlug={project.organizationSlug}
                                                            onTaskUpdate={handleTaskUpdate}
                                                            onSelectionChange={handleTaskSelection}
                                                            isSelected={selectedTasks.has(task.id)}
                                                            isDragging={snapshot.isDragging}
                                                            selectedCount={selectedTasks.has(task.id) ? selectedTasks.size : 0}
                                                            isSelectedButNotDragging={selectedTasks.has(task.id) && !snapshot.isDragging && isDraggingAny && selectedTasks.size > 1}
                                                        />
                                                    </div>
                                            <ScrollBar orientation="horizontal" />
                                        </ScrollArea>
                                                )}
                                            </Draggable>
                                            ))}
                                            {provided.placeholder}
                                        </div>
                                )}
                            </Droppable>
                            <QuickAddTask
                                status="todo"
                                projectId={project.id}
                                organizationSlug={project.organizationSlug}
                                assigneeId="unassigned"
                                onTaskCreated={handleTaskCreated}
                                alwaysShow={true}
                                />
                        </div>
                    </CardContent>
                </Card>

                {/* Load Next Week Button */}
                {/* <div className="flex justify-center">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={loadNextWeek}
                        disabled={loadingNext}
                        className="w-[200px]"
                    >
                        {loadingNext ? (
                            "Loading..."
                        ) : (
                            <>
                                <ChevronDown className="w-4 h-4 mr-2" />
                                Next Week
                            </>
                        )}
                    </Button>
                </div> */}

                {/* Backlog Section */}
                {showBacklogColumn && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg">Backlog</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2">
                            <Droppable droppableId="backlog">
                                {(provided) => (
                                    <div
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                        className="space-y-2"
                                    >
                                        {backlogTasks.map((task, index) => (
                                            <Draggable
                                                key={task.id}
                                                draggableId={task.id}
                                                index={index}
                                            >
                                                {(provided, snapshot) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                    >
                                                        <TaskListItem
                                                            task={task}
                                                            organizationSlug={project.organizationSlug}
                                                            onTaskUpdate={handleTaskUpdate}
                                                            onSelectionChange={handleTaskSelection}
                                                            isSelected={selectedTasks.has(task.id)}
                                                            isDragging={snapshot.isDragging}
                                                            selectedCount={selectedTasks.has(task.id) ? selectedTasks.size : 0}
                                                            isSelectedButNotDragging={selectedTasks.has(task.id) && !snapshot.isDragging && isDraggingAny && selectedTasks.size > 1}
                                                        />
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                            <QuickAddTask
                                status="backlog"
                                projectId={project.id}
                                organizationSlug={project.organizationSlug}
                                assigneeId="unassigned"
                                onTaskCreated={handleTaskCreated}
                                alwaysShow={true}
                            />
                        </div>
                        </CardContent>
                    </Card>
                )}
            </div>
        </DragDropContext>
    );
}